const toNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getPosizioneMatchData = (posizione = {}) => ({
  tecnicaCodice: posizione.tecnica?.codice || null,
  posizioneCodice: posizione.posizione?.codice || null,
  coloriCodice: posizione.numeroColoriPosizione?.codice || null,
  dimensioneCodice: posizione.dimensione?.codice || null,
})

// Normalizza la personalizzazione fornitore nel nuovo formato con lista posizioni.
const normalizePersonalizzazioneFornitore = (personalizzazione = {}) => {
  const posizioneUno =
    personalizzazione.posizioni?.find((posizione) => Number(posizione?.numero) === 1) || {}
  const posizioneDue =
    personalizzazione.posizioni?.find((posizione) => Number(posizione?.numero) === 2) || {}

  const matchUno = getPosizioneMatchData(posizioneUno)
  const matchDue = getPosizioneMatchData(posizioneDue)

  return {
    ...personalizzazione,
    tecnicaCodiceUno: matchUno.tecnicaCodice,
    posizioneUnoCodice: matchUno.posizioneCodice,
    posizioneUnoColoriCodice: matchUno.coloriCodice,
    posizioneUnoDimensioneCodice: matchUno.dimensioneCodice,
    tecnicaCodiceDue: matchDue.tecnicaCodice,
    posizioneDueCodice: matchDue.posizioneCodice,
    posizioneDueColoriCodice: matchDue.coloriCodice,
    posizioneDueDimensioneCodice: matchDue.dimensioneCodice,
    valorePersonalizzazione:
      personalizzazione.valorePersonalizzazione ?? personalizzazione.totalePersonalizzazione ?? 0,
    valoreConfezionamento:
      personalizzazione.valoreConfezionamento ?? personalizzazione.totaleConfezionamento ?? 0,
  }
}

export const getPersonalizzazioniFromRigheOrdine = (
  ordine,
  usaOrdineSg = false,
  usaPersonalizzazioniSg = false
) => {
  // Adatta le personalizzazioni della struttura corrente alle props attese dai componenti PDF.
  // Questi componenti espongono ancora le due posizioni come campi separati.
  const getPosizioneKey = (posizione) =>
    [
      posizione?.codice_tecnica || posizione?.tecnica?.codice || '',
      posizione?.codice_posizione || posizione?.posizione?.codice || '',
      posizione?.codice_numero_colori_posizione || posizione?.numero_colori_posizione?.codice || '',
      posizione?.codice_dimensione || posizione?.dimensione?.codice || '',
    ].join('|')
  const getPersonalizzazioneKey = (personalizzazione) => {
    const posizioni = personalizzazione.posizioni_personalizzazione_ordine || []
    const posizioneUno = posizioni.find((posizione) => Number(posizione.numero) === 1)
    const posizioneDue = posizioni.find((posizione) => Number(posizione.numero) === 2)

    return [
      personalizzazione.nome_lavorazione,
      getPosizioneKey(posizioneUno),
      getPosizioneKey(posizioneDue),
    ].join('|')
  }
  const ordinePersonalizzazioni = usaPersonalizzazioniSg ? ordine?.ordine_sg : ordine
  const personalizzazioniSg = new Map(
    (usaOrdineSg && !usaPersonalizzazioniSg
      ? ordine?.ordine_sg?.personalizzazioni_ordine || []
      : []
    ).map((personalizzazione) => [getPersonalizzazioneKey(personalizzazione), personalizzazione])
  )

  return (ordinePersonalizzazioni?.personalizzazioni_ordine || []).map((personalizzazione) => {
    const posizioni = personalizzazione.posizioni_personalizzazione_ordine || []
    const getPosizione = (numero) =>
      posizioni.find((posizione) => Number(posizione.numero) === numero) || null
    const normalizzaPosizione = (posizione) => {
      if (!posizione) return null

      return {
        ...posizione,
        logo_url: posizione.logo?.url || null,
        logo_name: posizione.logo?.name || null,
        aree_stampa: posizione.area_stampa ? [posizione.area_stampa] : [],
      }
    }
    // Per le personalizzazioni gestite da SIPEC manteniamo configurazione e
    // loghi SIPEC, ma usiamo le righe SG abbinate dalla configurazione logica.
    const personalizzazioneSg = personalizzazioniSg.get(getPersonalizzazioneKey(personalizzazione))
    const righe = (personalizzazioneSg?.righe_ordine || personalizzazione.righe_ordine || []).map(
      (riga) => ({
        ...riga,
        nome_lavorazione: personalizzazione.nome_lavorazione,
        prezzo_totale_netto: riga.prezzo_totale_netto ?? riga.totale,
        confezionamento_richiesto_personalizzazione:
          personalizzazione.confezionamento_richiesto_personalizzazione,
      })
    )
    const posizioneUno = normalizzaPosizione(getPosizione(1))
    const posizioneDue = normalizzaPosizione(getPosizione(2))
    const bozzaDigitaleThumb = posizioni.find((posizione) => posizione.bozza_digitale_image)
      ?.bozza_digitale_image?.url

    return {
      ...personalizzazione,
      righe_ordine: righe.map((riga) => ({
        ...riga,
        bozza_digitale_thumb: bozzaDigitaleThumb || riga.bozza_digitale_thumb,
      })),
      articolo: righe[0]?.articolo,
      personalizzazione_posizione_1: posizioneUno,
      personalizzazione_posizione_2: posizioneDue,
      prezzo_personalizzazione: righe.reduce(
        (totale, riga) => totale + toNumber(riga.prezzo_personalizzazione),
        0
      ),
      stesso_logo: !!posizioneUno?.logo_url && posizioneUno.logo_url === posizioneDue?.logo_url,
    }
  })
}

export const getPersonalizzazioniFornitoreFromRigheOrdine = (ordine) => {
  let p = []
  ;(ordine?.personalizzazioniOrdine || []).forEach((personalizzazione) => {
    const pRiga = normalizePersonalizzazioneFornitore(personalizzazione)

    const index = p.findIndex((value) => {
      return (
        value.nomeLavorazione === pRiga.nomeLavorazione &&
        value.tecnicaCodiceUno === pRiga.tecnicaCodiceUno &&
        value.posizioneUnoCodice === pRiga.posizioneUnoCodice &&
        value.posizioneUnoColoriCodice === pRiga.posizioneUnoColoriCodice &&
        value.posizioneUnoDimensioneCodice === pRiga.posizioneUnoDimensioneCodice &&
        value.tecnicaCodiceDue === pRiga.tecnicaCodiceDue &&
        value.posizioneDueCodice === pRiga.posizioneDueCodice &&
        value.posizioneDueColoriCodice === pRiga.posizioneDueColoriCodice &&
        value.posizioneDueDimensioneCodice === pRiga.posizioneDueDimensioneCodice
      )
    })
    if (index === -1) {
      p.push(pRiga)
    } else {
      p[index]['valorePersonalizzazione'] += toNumber(pRiga['valorePersonalizzazione'])
      p[index]['valoreConfezionamento'] += toNumber(pRiga['valoreConfezionamento'])
    }
  })
  return p
}

export const getRigheOrdineFromPersonalizzazione = (personalizzazione) => {
  return personalizzazione.righe_ordine || []
}
